<template>
  <drop-down
    id="tzDropdown"
    :options="dropdownOptions"
    :selected-options="selectedOptions"
    :label="action.label"
    :show-select-all="false"
    :show-popup-header="true"
    :use-groups="true"
    :use-checkboxes="false"
    :is-mandatory="true"
    :enable-search="true"
    :align-drop-down-to-right="alignToRight"
    drop-down-container-width="26rem"
    @selection-changed="optionSelected" />
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';

  import ChartTimezoneGroupActionsUtils from '@/common/components/chart/chart-timezone-group-actions';
  import { DropDown } from '@/common/components/controls';
  import ChartActionId from '@/common/enums/chart-action-id.enum';
  import ChartEvents from '@/common/enums/chart-events.enum';
  import LocalCache from '@/utils/LocalCache';
  import i18n from '@/vueI18n';

  const props = defineProps<{
    action: { options: { isSelected: boolean; label: string; id: string; enabled: boolean }[] };
    alignToRight: boolean;
    localCacheKey: string | null;
  }>();

  const emit = defineEmits([ChartEvents.TIMEZONE_SELECTED]);

  const suggestedTimezones = ref(ChartTimezoneGroupActionsUtils.suggestedTz());

  const dropdownOptions = computed(() => {
    if (props.action?.options?.length === 0) return [];

    const options = [
      {
        name: i18n.global.t('names.timezoneSuggested', { n: 2 }),
        children: props.action.options
          .filter((o) => suggestedTimezones.value.indexOf(o.id) > -1)
          .map((o) => ({ isDefault: o.isSelected, name: o.label, id: o.id })),
      },
    ];

    const timezones = props.action.options
      .filter((o) => !suggestedTimezones.value.includes(o.id))
      .map((o) => ({ isDefault: o.isSelected, name: o.label, id: o.id }));

    if (timezones?.length > 0) {
      options.push({
        name: i18n.global.t('names.timezone', { n: 2 }),
        children: timezones,
      });
    }

    return options;
  });

  const selectedOptions = computed(() => {
    if (props.action?.options?.length === 0) return [];

    return props.action.options
      .filter((o) => o.isSelected)
      .map((o) => ({ isDefault: o.isSelected, name: o.label, id: o.id }));
  });

  const optionSelected = (option: { included: { id: string }[] }) => {
    const savedSettings = LocalCache.get(props.localCacheKey);
    const newSavedSettings = [];

    if (savedSettings !== undefined && savedSettings !== null) {
      savedSettings.forEach((stg: any) => {
        if (stg.id === ChartActionId.TIMEZONE) {
          newSavedSettings.push({ id: stg.id, optionSelected: option.included[0].id });
        } else {
          newSavedSettings.push(stg);
        }
      });
    } else {
      newSavedSettings.push({ id: ChartActionId.TIMEZONE, optionSelected: option.included[0].id });
    }

    LocalCache.set(props.localCacheKey, newSavedSettings);

    const selectedOption = props.action.options.find((o) => option.included[0].id === o.id);
    emit(ChartEvents.TIMEZONE_SELECTED, selectedOption);
  };
</script>

<style lang="scss">
  @use '@/styles/variables' as *;

  #dropdown {
    .clear-btn {
      display: none;
    }

    .drop-down {
      .btn-drop-down {
        border-radius: 0;
      }
    }
  }

  #app #tzDropdown.drop-down .drop-down-popup-container {
    width: auto;
  }

  #app #tzDropdown.drop-down .btn-drop-down.tinted-success {
    border-radius: 0;
    transition: all 250ms;

    border: 0.1rem solid var(--defaultButtonBorderColor);
    background: white;
    font-weight: 500;
    color: var(--textColor);

    &:hover {
      transition: all 500ms;

      background: white;
      font-weight: 500;
      color: var(--textColor);
    }

    span.label {
      line-height: 2.5rem;

      &:after {
        content: '▼';
        margin-left: 1rem;
        font-size: 1rem;
      }
    }
  }
</style>
